/**
 * Applicant pipeline stages (ApplicantStack-style), behind the flag `applicant_pipeline_v2`.
 *
 * Stages: New, Manager Review, Phone Screen, Interview, Reference Check, Job Offer,
 * Hired (Orientation Pending), plus "Not moving forward" which can be set at any stage.
 *
 * Data model (additive, on applications/{applicationId}; employer fields, written by employers in
 * the job's org under the existing update rule):
 *   stage:            one of PIPELINE_STAGES (absent on every application written before this)
 *   notMovingForward: { at, by, reason? } | null
 *   stageHistory:     [{ stage, at, by }] appended on every move, capped at STAGE_HISTORY_CAP
 *
 * The stored `status` ('new' | 'reviewed' | 'interview_scheduled' | 'offered' | 'rejected' |
 * 'archived') is never renamed or dropped: server code and the candidate's own screens depend on
 * it (scheduling moves new/reviewed to interview_scheduled when an interview is booked,
 * onOfferCreate sets 'offered'). A stage move writes a status that stays true: it keeps the
 * current status when it already fits the target stage and otherwise falls back to the stage's
 * plain baseline. It never claims an interview or an offer that does not exist.
 *
 * Reading: resolvePipelineStage() works for every application, with or without `stage`:
 *   1. "Not moving forward" when notMovingForward is set or status is rejected / archived. A
 *      turned-down application stays turned down even when the person was hired for ANOTHER job in
 *      the org (2026-10-13: before, a person hired on job A also showed as Hired on job B, where
 *      they had been rejected, so per-job Hired counts double counted);
 *   2. Hired (Orientation Pending) when this application is the one that led to a hire under the
 *      ONE hire rule (src/lib/hiring/hiredThroughArevina.ts; attributeHiresToApplications below
 *      picks the application);
 *   3. "Not moving forward" (reason "Offer declined" / "Offer withdrawn") when the latest offer on
 *      this application was declined or withdrawn and nothing has moved the applicant since
 *      (status still 'offered', no stage move after the answer). Before, a declined offer stayed
 *      at Job Offer forever, because nothing rewrites the application when the candidate answers;
 *   4. otherwise the later of the stored stage and the stage the status implies, so an interview
 *      booked or an offer sent through the existing flows moves the applicant forward without any
 *      server change.
 *
 * Pure: no Firebase imports. Copied into functions/src/generated/ by
 * scripts/syncFunctionsGenerated.mjs so server code can read stages with the same rule.
 */

export const APPLICANT_PIPELINE_FLAG = 'applicant_pipeline_v2';
export const APPLICANT_PIPELINE_FLAG_LABEL = 'Applicant pipeline stages';
export const APPLICANT_PIPELINE_FLAG_DESCRIPTION =
  'Employers move applicants through stages (New applicant, Manager Review, Phone Screen, Interview, Reference Check, Job Offer, Hired (Orientation Pending)) or mark them Not moving forward, one at a time or in bulk, with stage counts per job. Off: the existing status screens.';

/** Owned by the "Request documents" slice; read here only to decide whether its button may show. */
export const DOCUMENT_REQUESTS_FLAG = 'document_requests';

export const PIPELINE_STAGES = [
  'new',
  'manager_review',
  'phone_screen',
  'interview',
  'reference_check',
  'job_offer',
  'hired_orientation_pending',
] as const;

export type PipelineStage = (typeof PIPELINE_STAGES)[number];

export const PIPELINE_STAGE_LABELS: Record<PipelineStage, string> = {
  // 'New' alone meant nothing on the employer side (2026-10-20): one person is a 'New applicant'.
  new: 'New applicant',
  manager_review: 'Manager Review',
  phone_screen: 'Phone Screen',
  interview: 'Interview',
  reference_check: 'Reference Check',
  job_offer: 'Job Offer',
  hired_orientation_pending: 'Hired (Orientation Pending)',
};

export const NOT_MOVING_FORWARD = 'not_moving_forward' as const;
export const NOT_MOVING_FORWARD_LABEL = 'Not moving forward';

/** A count bucket: one of the stages, or "Not moving forward". */
export type PipelineBucket = PipelineStage | typeof NOT_MOVING_FORWARD;
export const PIPELINE_BUCKETS: readonly PipelineBucket[] = [...PIPELINE_STAGES, NOT_MOVING_FORWARD];

/**
 * Hired, for a group of people (2026-10-16): a column, filter or count holds people whose
 * orientation is done and people still waiting, so it reads "Hired". One person's own label says
 * "Hired (Orientation Pending)" only while their orientation is pending (resolvedStageLabel).
 */
export const HIRED_LABEL = 'Hired';
/** New, for a group of people (a column, filter or count): 'New applicants' (2026-10-20). */
export const NEW_APPLICANTS_LABEL = 'New applicants';

/** A count bucket's name (columns, filters, chips, counts). */
export function pipelineBucketLabel(bucket: PipelineBucket): string {
  if (bucket === NOT_MOVING_FORWARD) return NOT_MOVING_FORWARD_LABEL;
  if (bucket === 'new') return NEW_APPLICANTS_LABEL;
  return bucket === 'hired_orientation_pending' ? HIRED_LABEL : PIPELINE_STAGE_LABELS[bucket];
}

/** One applicant's stage in words: Hired without "(Orientation Pending)" once their orientation is done. */
export function resolvedStageLabel(resolved: Pick<ResolvedPipeline, 'stage' | 'notMovingForward' | 'orientationDone'>): string {
  if (resolved.notMovingForward) return NOT_MOVING_FORWARD_LABEL;
  const stage = resolved.stage ?? 'new';
  if (stage === 'hired_orientation_pending' && resolved.orientationDone) return HIRED_LABEL;
  return PIPELINE_STAGE_LABELS[stage];
}

export const STAGE_HISTORY_CAP = 50;
export const NOT_MOVING_FORWARD_REASON_MAX = 500;

export type PipelineHistoryStage = PipelineStage | typeof NOT_MOVING_FORWARD;

/** `at` is whatever timestamp type the caller writes (a Firestore Timestamp in the app). */
export type StageHistoryEntry<T = unknown> = { stage: PipelineHistoryStage; at: T; by: string };
export type NotMovingForwardRecord<T = unknown> = { at: T; by: string; reason?: string };

/** The fields an application needs for stage resolution (any extra fields are ignored). */
export type PipelineApplicationLike = {
  stage?: unknown;
  status?: unknown;
  notMovingForward?: unknown;
  stageHistory?: unknown;
};

/** Stored statuses this module writes. */
export type PipelineWritableStatus = 'new' | 'reviewed' | 'interview_scheduled' | 'offered' | 'rejected';

export function isPipelineStage(value: unknown): value is PipelineStage {
  return typeof value === 'string' && (PIPELINE_STAGES as readonly string[]).includes(value);
}

export function stageIndex(stage: PipelineStage): number {
  return PIPELINE_STAGES.indexOf(stage);
}

/** Status string from Firestore (string, or the legacy `{ name }` object). */
export function statusString(status: unknown): string | null {
  if (typeof status === 'string') return status.trim() || null;
  if (status && typeof status === 'object' && 'name' in status) {
    const name = (status as { name?: unknown }).name;
    return typeof name === 'string' && name.trim() ? name.trim() : null;
  }
  return null;
}

type StatusMapping =
  | { kind: 'stage'; stage: PipelineStage }
  | { kind: 'not_moving_forward'; archived: boolean }
  | { kind: 'unknown' };

/**
 * What a stored status means as a stage. Covers the 6 real top-level statuses, the older
 * 4-value employer UI set (sent / under_review / interview / rejected) and the legacy values the
 * scheduling code still accepts (pending / applied) and the old mirror's 'hired'.
 * A missing status reads as 'new' (onApplicationCreate stamps 'new' right after create).
 */
export function stageForStatus(status: unknown): StatusMapping {
  const s = statusString(status);
  switch (s) {
    case null:
    case 'new':
    case 'pending':
    case 'applied':
    case 'sent':
      return { kind: 'stage', stage: 'new' };
    case 'reviewed':
    case 'under_review':
      return { kind: 'stage', stage: 'manager_review' };
    case 'interview_scheduled':
    case 'interview':
      return { kind: 'stage', stage: 'interview' };
    case 'offered':
      return { kind: 'stage', stage: 'job_offer' };
    case 'hired':
      return { kind: 'stage', stage: 'hired_orientation_pending' };
    case 'rejected':
      return { kind: 'not_moving_forward', archived: false };
    case 'archived':
      return { kind: 'not_moving_forward', archived: true };
    default:
      return { kind: 'unknown' };
  }
}

export type ResolvedPipeline = {
  /** Where the applicant is (or was, when not moving forward). Null only when not moving forward with no known stage. */
  stage: PipelineStage | null;
  notMovingForward: boolean;
  /** Status 'archived' (inactive) without an explicit not-moving-forward record. */
  archived: boolean;
  /** The count bucket: the stage, or 'not_moving_forward'. */
  bucket: PipelineBucket;
  /** What decided it ('workforce' = the hire rule, 'offer' = a declined or withdrawn offer). */
  source: 'not_moving_forward' | 'workforce' | 'offer' | 'stage' | 'status';
  /** False only when the stored status is a value this module does not recognise (the audit reports these). */
  mappable: boolean;
  reason: string | null;
  /** Hired and orientation done (ResolveOptions.orientationDone); false at every other stage. */
  orientationDone?: boolean;
};

/** The latest offer sent on an application (offers/{id}: status 'sent' | 'accepted' | 'declined' | 'withdrawn'). */
export type PipelineOfferFact = { status: string | null; /** When it was answered or last changed. */ atMs: number | null };

export type ResolveOptions = {
  /**
   * This application is the one that led to a hire under the ONE hire rule (accepted team
   * invitation or accepted offer, not external, not "Didn't start"); see
   * attributeHiresToApplications. Older callers pass "this person is hired in the org": a rejected
   * or archived application still reads Not moving forward, so that cannot double count a person
   * who was turned down for one job and hired for another.
   */
  hiredInOrg?: boolean;
  /** The latest offer on this application, when the caller has it. */
  offer?: PipelineOfferFact | null;
  /** With hiredInOrg: the person's orientation is done (orientationDoneFor), so they read "Hired". */
  orientationDone?: boolean;
};

export const OFFER_DECLINED_REASON = 'Offer declined';
export const OFFER_WITHDRAWN_REASON = 'Offer withdrawn';

function nmfRecord(value: unknown): { reason: string | null } | null {
  if (!value || typeof value !== 'object') return null;
  const reason = (value as { reason?: unknown }).reason;
  return { reason: typeof reason === 'string' && reason.trim() ? reason.trim() : null };
}

/** Milliseconds from a Firestore Timestamp, { seconds }, Date, number or ISO string; null otherwise. */
export function pipelineTimeMs(v: unknown): number | null {
  if (v == null) return null;
  const t = v as { toMillis?: () => number; seconds?: number; _seconds?: number };
  if (typeof t.toMillis === 'function') {
    const n = t.toMillis();
    return Number.isFinite(n) ? n : null;
  }
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (v instanceof Date) return Number.isFinite(v.getTime()) ? v.getTime() : null;
  if (typeof t.seconds === 'number') return t.seconds * 1000;
  if (typeof t._seconds === 'number') return t._seconds * 1000;
  if (typeof v === 'string') {
    const n = Date.parse(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** When the employer last moved this applicant (newest stageHistory entry), or null. */
function lastStageMoveMs(app: PipelineApplicationLike): number | null {
  if (!Array.isArray(app.stageHistory)) return null;
  let best: number | null = null;
  for (const e of app.stageHistory as unknown[]) {
    const ms = pipelineTimeMs((e as { at?: unknown } | null)?.at);
    if (ms != null && (best == null || ms > best)) best = ms;
  }
  return best;
}

/** A declined / withdrawn offer that is still the last word on this application (see header, rule 3). */
function closedOfferReason(app: PipelineApplicationLike, offer: PipelineOfferFact | null | undefined): string | null {
  if (!offer || (offer.status !== 'declined' && offer.status !== 'withdrawn')) return null;
  if (statusString(app.status) !== 'offered') return null;
  const moved = lastStageMoveMs(app);
  if (moved != null && offer.atMs != null && moved > offer.atMs) return null;
  return offer.status === 'declined' ? OFFER_DECLINED_REASON : OFFER_WITHDRAWN_REASON;
}

export function resolvePipelineStage(app: PipelineApplicationLike, opts: ResolveOptions = {}): ResolvedPipeline {
  const stored = isPipelineStage(app.stage) ? app.stage : null;
  const fromStatus = stageForStatus(app.status);
  const nmf = nmfRecord(app.notMovingForward);
  const mappable = fromStatus.kind !== 'unknown';

  if (nmf) {
    return { stage: stored ?? (fromStatus.kind === 'stage' ? fromStatus.stage : null), notMovingForward: true, archived: false, bucket: NOT_MOVING_FORWARD, source: 'not_moving_forward', mappable, reason: nmf.reason };
  }
  if (fromStatus.kind === 'not_moving_forward') {
    return { stage: stored, notMovingForward: true, archived: fromStatus.archived, bucket: NOT_MOVING_FORWARD, source: 'status', mappable: true, reason: null };
  }
  if (opts.hiredInOrg) {
    return { stage: 'hired_orientation_pending', notMovingForward: false, archived: false, bucket: 'hired_orientation_pending', source: 'workforce', mappable, reason: null, orientationDone: opts.orientationDone === true };
  }
  const offerClosed = closedOfferReason(app, opts.offer);
  if (offerClosed) {
    return { stage: 'job_offer', notMovingForward: true, archived: false, bucket: NOT_MOVING_FORWARD, source: 'offer', mappable, reason: offerClosed };
  }
  const statusStage = fromStatus.kind === 'stage' ? fromStatus.stage : null;
  if (stored && (!statusStage || stageIndex(stored) >= stageIndex(statusStage))) {
    return { stage: stored, notMovingForward: false, archived: false, bucket: stored, source: 'stage', mappable, reason: null };
  }
  const stage = statusStage ?? stored ?? 'new';
  return { stage, notMovingForward: false, archived: false, bucket: stage, source: 'status', mappable, reason: null };
}

/** Rank of a live (not rejected / archived) status. */
const LIVE_STATUS_RANK: Record<string, number> = { new: 0, reviewed: 1, interview_scheduled: 2, offered: 3 };

/** For each stage: the plain baseline status, and the furthest status that is still true at that stage. */
const STAGE_STATUS_RANGE: Record<PipelineStage, { base: PipelineWritableStatus; max: PipelineWritableStatus }> = {
  new: { base: 'new', max: 'new' },
  manager_review: { base: 'reviewed', max: 'reviewed' },
  phone_screen: { base: 'reviewed', max: 'reviewed' },
  interview: { base: 'reviewed', max: 'interview_scheduled' },
  reference_check: { base: 'reviewed', max: 'interview_scheduled' },
  job_offer: { base: 'reviewed', max: 'offered' },
  hired_orientation_pending: { base: 'reviewed', max: 'offered' },
};

/**
 * The status to store when an applicant is moved to `stage`. Keeps the current status when it is
 * already true at that stage (an interview that was booked stays 'interview_scheduled', a sent
 * offer stays 'offered'); otherwise the stage's baseline. Moving to Interview does not claim an
 * interview: the scheduling flow sets 'interview_scheduled' when one is actually booked.
 */
export function statusForStage(stage: PipelineStage, currentStatus: unknown): PipelineWritableStatus {
  const range = STAGE_STATUS_RANGE[stage];
  const cur = statusString(currentStatus);
  const rank = cur !== null && cur in LIVE_STATUS_RANK ? LIVE_STATUS_RANK[cur] : null;
  if (rank !== null && rank >= LIVE_STATUS_RANK[range.base] && rank <= LIVE_STATUS_RANK[range.max]) {
    return cur as PipelineWritableStatus;
  }
  return range.base;
}

function existingHistory<T>(app: PipelineApplicationLike): StageHistoryEntry<T>[] {
  if (!Array.isArray(app.stageHistory)) return [];
  return (app.stageHistory as unknown[]).filter(
    (e): e is StageHistoryEntry<T> =>
      !!e && typeof e === 'object' && typeof (e as { by?: unknown }).by === 'string' &&
      (isPipelineStage((e as { stage?: unknown }).stage) || (e as { stage?: unknown }).stage === NOT_MOVING_FORWARD),
  );
}

/** Append one entry, keeping only the newest STAGE_HISTORY_CAP. */
export function appendStageHistory<T>(app: PipelineApplicationLike, entry: StageHistoryEntry<T>): StageHistoryEntry<T>[] {
  const next = [...existingHistory<T>(app), entry];
  return next.length > STAGE_HISTORY_CAP ? next.slice(next.length - STAGE_HISTORY_CAP) : next;
}

export type StageMoveUpdate<T> = {
  stage: PipelineStage;
  status: PipelineWritableStatus;
  notMovingForward: null;
  stageHistory: StageHistoryEntry<T>[];
};

/**
 * The fields to write when moving an applicant to `target` (also used to bring someone who was
 * not moving forward back into the pipeline). Clears notMovingForward.
 */
export function planStageMove<T>(app: PipelineApplicationLike, target: PipelineStage, by: string, at: T): StageMoveUpdate<T> {
  if (!isPipelineStage(target)) throw new Error(`Unknown pipeline stage: ${String(target)}`);
  if (!by) throw new Error('A stage move needs the uid of the person making it');
  const fromStatusIsClosed = (() => {
    const s = statusString(app.status);
    return s === 'rejected' || s === 'archived';
  })();
  return {
    stage: target,
    status: statusForStage(target, fromStatusIsClosed ? 'new' : app.status),
    notMovingForward: null,
    stageHistory: appendStageHistory(app, { stage: target, at, by }),
  };
}

export type NotMovingForwardUpdate<T> = {
  stage: PipelineStage;
  status: 'rejected';
  notMovingForward: NotMovingForwardRecord<T>;
  stageHistory: StageHistoryEntry<T>[];
};

export function cleanNotMovingForwardReason(reason: unknown): string | null {
  if (typeof reason !== 'string') return null;
  const t = reason.replace(/\s+/g, ' ').trim();
  return t ? t.slice(0, NOT_MOVING_FORWARD_REASON_MAX) : null;
}

/**
 * The fields to write for "Not moving forward" (any stage). Keeps the stage they were at, stores
 * status 'rejected' (what every existing screen already understands), and records who and when.
 * The candidate message is a separate, optional action and never part of this write.
 */
export function planNotMovingForward<T>(app: PipelineApplicationLike, by: string, at: T, reason?: unknown, opts: ResolveOptions = {}): NotMovingForwardUpdate<T> {
  if (!by) throw new Error('Not moving forward needs the uid of the person making it');
  const current = resolvePipelineStage({ ...app, notMovingForward: null }, opts);
  const r = cleanNotMovingForwardReason(reason);
  return {
    stage: current.stage ?? 'new',
    status: 'rejected',
    notMovingForward: r ? { at, by, reason: r } : { at, by },
    stageHistory: appendStageHistory(app, { stage: NOT_MOVING_FORWARD, at, by }),
  };
}

/**
 * What the CANDIDATE sees for their own application (2026-10-15, tester fixes A1). Before, the EMT's
 * screens read only the stored status, so most employer moves never showed (an applicant moved to
 * Reference Check or Hired still read "In Review"), and the EMT dashboard's map only knew the old
 * 4-value statuses, so real applications showed the raw word ("reviewed"). The candidate never sees
 * the team's private reason, and nothing claims an interview or an offer that does not exist:
 * Interview without a booking reads "Interview stage", Job Offer without a sent offer "Final stage".
 * Stored stage + status only (the hire rule needs the org's records, which a candidate cannot read;
 * a hire made from the pipeline stores stage hired_orientation_pending, which reads "Hired").
 */
export type CandidateStageTone = 'neutral' | 'progress' | 'good' | 'closed';
export function candidateStageView(app: PipelineApplicationLike): { label: string; tone: CandidateStageTone; bucket: PipelineBucket | 'archived' } {
  const status = statusString(app.status);
  if (status === 'archived' && !nmfRecord(app.notMovingForward)) return { label: 'Closed', tone: 'closed', bucket: 'archived' };
  const r = resolvePipelineStage(app);
  if (r.notMovingForward) return { label: 'Not moving forward', tone: 'closed', bucket: NOT_MOVING_FORWARD };
  switch (r.stage) {
    case 'manager_review':
      return { label: 'In review', tone: 'progress', bucket: 'manager_review' };
    case 'phone_screen':
      return { label: 'Phone screen', tone: 'progress', bucket: 'phone_screen' };
    case 'interview':
      return { label: status === 'interview_scheduled' ? 'Interview scheduled' : 'Interview stage', tone: 'progress', bucket: 'interview' };
    case 'reference_check':
      return { label: 'Checking references', tone: 'progress', bucket: 'reference_check' };
    case 'job_offer':
      return { label: status === 'offered' ? 'Offer sent' : 'Final stage', tone: 'good', bucket: 'job_offer' };
    case 'hired_orientation_pending':
      return { label: 'Hired', tone: 'good', bucket: 'hired_orientation_pending' };
    default:
      return { label: 'Submitted', tone: 'neutral', bucket: 'new' };
  }
}

/** What the employer UI should open after a move to `target`. */
export function stageHandoff(target: PipelineStage): 'schedule_interview' | 'orientation' | null {
  if (target === 'interview') return 'schedule_interview';
  if (target === 'hired_orientation_pending') return 'orientation';
  return null;
}

export function emptyBucketCounts(): Record<PipelineBucket, number> {
  return Object.fromEntries(PIPELINE_BUCKETS.map((b) => [b, 0])) as Record<PipelineBucket, number>;
}

/** A caller's per-application answer: a plain "hired" boolean (older callers) or full ResolveOptions. */
export type PipelineOptsFor<A> = (app: A) => boolean | ResolveOptions;

export function resolveOptsOf<A>(optsFor: PipelineOptsFor<A> | undefined, app: A): ResolveOptions {
  if (!optsFor) return {};
  const o = optsFor(app);
  return typeof o === 'boolean' ? { hiredInOrg: o } : o || {};
}

/** Count applications per bucket. `optsFor(app)`: whether this application is the hire (and its latest offer). */
export function countPipelineBuckets<A extends PipelineApplicationLike>(apps: readonly A[], optsFor?: PipelineOptsFor<A>): Record<PipelineBucket, number> {
  const counts = emptyBucketCounts();
  for (const app of apps) counts[resolvePipelineStage(app, resolveOptsOf(optsFor, app)).bucket] += 1;
  return counts;
}

/* ------------------------------ which application is the hire ------------------------------ */

/** What the pipeline needs about one person hired at the org (OrgHire in hiredThroughArevina.ts fits). */
export type PipelineHireFact = { hiredAtMs: number | null; offerApplicationIds?: readonly string[] };

export type AttributableApplication = PipelineApplicationLike & { id: string; emtId?: unknown; createdAt?: unknown };

/**
 * Which application led to each hire (2026-10-13). A workforce record has no job or application on
 * it, so a hire is tied to ONE of the person's applications to the organization:
 *   1. the application whose offer they accepted, when there is one;
 *   2. else, among the applications that are still live (not turned down, no declined offer) and
 *      were sent before the hire, the one furthest along the pipeline (ties: the newest);
 *   3. else none: every application was turned down, or they only applied after being hired, so
 *      the hire came from somewhere else (the orientation list or a direct team invitation). It
 *      still counts in Total Hires and on Results, just not as Hired on a job's pipeline.
 * `apps` must be ALL of the person's applications to the org (any job), or the choice in rule 2
 * can land on the wrong job. Returns the chosen application ids.
 */
export function attributeHiresToApplications(
  apps: readonly AttributableApplication[],
  hires: ReadonlyMap<string, PipelineHireFact>,
  offers?: ReadonlyMap<string, PipelineOfferFact>,
): Set<string> {
  const byPerson = new Map<string, AttributableApplication[]>();
  for (const a of apps) {
    const emtId = typeof a.emtId === 'string' ? a.emtId : '';
    if (!emtId || !hires.has(emtId)) continue;
    byPerson.set(emtId, [...(byPerson.get(emtId) ?? []), a]);
  }
  const out = new Set<string>();
  for (const [emtId, list] of byPerson) {
    const hire = hires.get(emtId)!;
    const created = (a: AttributableApplication) => pipelineTimeMs(a.createdAt);
    const viaOffer = list
      .filter((a) => (hire.offerApplicationIds ?? []).includes(a.id))
      .sort((x, y) => (created(y) ?? 0) - (created(x) ?? 0))[0];
    if (viaOffer) {
      out.add(viaOffer.id);
      continue;
    }
    const live = list
      .map((a) => ({ a, r: resolvePipelineStage(a, { offer: offers?.get(a.id) ?? null }) }))
      .filter(({ a, r }) => {
        if (r.notMovingForward) return false;
        const c = created(a);
        return hire.hiredAtMs == null || c == null || c <= hire.hiredAtMs;
      });
    if (live.length === 0) continue;
    live.sort((x, y) => stageIndex(y.r.stage ?? 'new') - stageIndex(x.r.stage ?? 'new') || (created(y.a) ?? 0) - (created(x.a) ?? 0));
    out.add(live[0].a.id);
  }
  return out;
}

/** Latest offer per application, from offers/{id} docs ({ applicationId, status, updatedAt, createdAt }). */
export function latestOfferByApplication(offers: ReadonlyArray<{ data: unknown }>): Map<string, PipelineOfferFact & { createdMs: number }> {
  const out = new Map<string, PipelineOfferFact & { createdMs: number }>();
  for (const o of offers) {
    const d = (o.data || {}) as Record<string, unknown>;
    const appId = typeof d.applicationId === 'string' ? d.applicationId : '';
    if (!appId) continue;
    const createdMs = pipelineTimeMs(d.createdAt) ?? 0;
    const prev = out.get(appId);
    if (prev && prev.createdMs > createdMs) continue;
    out.set(appId, {
      status: typeof d.status === 'string' ? d.status : null,
      atMs: pipelineTimeMs(d.respondedAt) ?? pipelineTimeMs(d.updatedAt) ?? createdMs,
      createdMs,
    });
  }
  return out;
}

/** Compact one-line summary, non-zero buckets only, e.g. "3 New, 1 Interview, 2 Not moving forward". */
export function formatBucketSummary(counts: Record<PipelineBucket, number>): string {
  return PIPELINE_BUCKETS.filter((b) => counts[b] > 0)
    .map((b) => `${counts[b]} ${pipelineBucketLabel(b)}`)
    .join(', ');
}

/** Default text for the optional candidate message (no em dashes, plain wording). */
export function defaultNotMovingForwardMessage(candidateName: string | null | undefined, jobTitle: string | null | undefined): string {
  const name = (candidateName || '').trim().split(/\s+/)[0] || 'there';
  const job = (jobTitle || '').trim();
  return `Hi ${name}, thank you for applying${job ? ` for ${job}` : ''}. We have decided not to move forward with your application at this time. We appreciate your interest and wish you the best.`;
}
